import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/shared/components/ui/Card';
import type { MorningSummaryAIInsight } from '../types';
import { getSeverityBadgeClass } from '../types';
import { formatDistanceToNow } from 'date-fns';

interface AIInsightsCardProps {
  farmId: string;
  insights: MorningSummaryAIInsight[];
}

const MAX_INSIGHTS = 3;

export const AIInsightsCard: React.FC<AIInsightsCardProps> = ({ farmId, insights }) => {
  // Only show high/critical severity insights
  const criticalInsights = insights
    .filter((i) => i.severity === 'critical' || i.severity === 'high')
    .slice(0, MAX_INSIGHTS);

  if (criticalInsights.length === 0) {
    return null;
  }

  const getAnalysisTypeLabel = (type: string): string => {
    switch (type) {
      case 'root_cause':
        return 'Root Cause';
      case 'health_diagnosis':
        return 'Diagnosis';
      default:
        return 'Analysis';
    }
  };

  const getAnalysisTypeColor = (type: string): string => {
    switch (type) {
      case 'root_cause':
        return 'bg-purple-100 text-purple-600';
      case 'health_diagnosis':
        return 'bg-rose-100 text-rose-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-500" />
          <CardTitle>AI Insights</CardTitle>
          <span className="ml-auto text-sm text-gray-500">{criticalInsights.length} recent</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {criticalInsights.map((insight) => (
            <Link
              key={insight.analysisId}
              to={`/farms/${farmId}/ai/${insight.analysisId}`}
              className="block p-3 rounded-lg border border-slate-200/50 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-start gap-3">
                {/* Analysis type and severity badges */}
                <div className="flex flex-col gap-1">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${getAnalysisTypeColor(insight.analysisType)}`}>
                    {getAnalysisTypeLabel(insight.analysisType)}
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full uppercase ${getSeverityBadgeClass(insight.severity)}`}>
                    {insight.severity}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{insight.pondName}</p>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{insight.summary}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDistanceToNow(new Date(insight.createdAt), { addSuffix: true })}
                  </p>
                </div>

                <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
              </div>
            </Link>
          ))}
        </div>

        {/* View all link */}
        <Link
          to={`/farms/${farmId}/ai/history`}
          className="flex items-center justify-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium mt-4"
        >
          View all AI analyses
          <ChevronRight className="h-4 w-4" />
        </Link>
      </CardContent>
    </Card>
  );
};
